import React from 'react'
import { DcButton, DcInput, PixelFactoryContext, PixelTable } from 'pixel-base'
import {
  createCostCenter,
  deleteCostCenter,
  getCostCenters,
  updateCostCenter,
} from 'utils/api-routes-accounts'
import FinanceListActions from '../../components/domain/FinanceListActions'
import FinancePageShell from '../../components/FinancePageShell'
import FinanceStatusBadge from '../../components/FinanceStatusBadge'
import FinanceEntityDrawer from '../../components/foundation/FinanceEntityDrawer'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { CostCenter } from '../../types/finance'

const defaultForm = {
  id: null,
  code: '',
  name: '',
  parent_id: '',
}

const CostCenters = () => {
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const costCenters = useFinanceObservable<CostCenter[]>(() => getCostCenters())
  const [isOpen, setIsOpen] = React.useState(false)
  const [form, setForm] = React.useState<any>(defaultForm)

  const reset = () => {
    setIsOpen(false)
    setForm(defaultForm)
  }

  const save = () => {
    const payload = {
      ...form,
      parent_id: form.parent_id ? Number(form.parent_id) : null,
    }
    const request = form.id
      ? updateCostCenter(form.id, payload)
      : createCostCenter(payload)
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(
          form.id ? 'Cost center updated' : 'Cost center created',
        )
        reset()
        costCenters.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Failed to save cost center'),
    })
  }

  const remove = (id: number) => {
    deleteCostCenter(id).subscribe({
      next: () => {
        pixelFactory?.showSuccess?.('Cost center deleted')
        costCenters.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Unable to delete cost center'),
    })
  }

  return (
    <>
      <FinancePageShell
        title="Cost Centers"
        isLoading={costCenters.loading}
        primaryAction={{
          label: 'Add Cost Center',
          onClick: () => setIsOpen(true),
        }}
      >
        <PixelTable
          columns={[
            { name: 'Code', selector: (row) => row.code },
            { name: 'Name', selector: (row) => row.name },
            { name: 'Parent', selector: (row) => row.parent_id || '--' },
            {
              name: 'Status',
              selector: (row) => (
                <FinanceStatusBadge status={row.is_active ?? true} />
              ),
            },
            {
              name: 'Actions',
              selector: (row) => (
                <FinanceListActions
                  actions={[
                    {
                      key: 'edit',
                      label: 'Edit',
                      tone: 'secondary',
                      onClick: () => {
                        setForm(row)
                        setIsOpen(true)
                      },
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      tone: 'danger',
                      onClick: () => remove(row.id),
                    },
                  ]}
                />
              ),
            },
          ]}
          data={costCenters.data || []}
          searchable
          pagination
        />
      </FinancePageShell>
      <FinanceEntityDrawer
        isOpen={isOpen}
        onClose={reset}
        title={form.id ? 'Edit Cost Center' : 'Create Cost Center'}
        width="32%"
      >
        <DcInput
          value={form.code}
          placeholder="Code"
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />
        <DcInput
          value={form.name}
          placeholder="Name"
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <DcInput
          value={form.parent_id}
          placeholder="Parent ID (optional)"
          onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
        />
        <DcButton onClick={save}>Save Cost Center</DcButton>
      </FinanceEntityDrawer>
    </>
  )
}

export default CostCenters
